import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { medium, colors, mediumInter } from '@/styles';
import { Menu, ArrowBig } from '@/svgs';
import { ClickableElement } from '@/atoms';

const iconSizes = {
  small: 4.86,
  medium: 5.32,
  large: 5.83,
};

const iconWidths = {
  small: '0.86625rem',
  medium: '0.94813rem',
  large: '1.03875rem',
};

const iconMargins = {
  small: '0.509375rem',
  medium: '0.588125rem',
  large: '0.4975rem',
};

/**
 * DropDown component
 *
 * @param {import('./DropDown').DropDownProps} props
 */
const DropDown = ({
  className,
  defaultValue,
  disabled,
  onOpen,
  onChange,
  menuItems,
  type,
  title,
  align,
  direction,
  size,
  isRebrand,
}) => {
  /** @type {React.MutableRefObject<HTMLDivElement|null>} */
  const dropdownRef = useRef(null);
  const [value, setValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [submenusOpen, setSubmenusOpen] = useState([]);

  useEffect(() => {
    /**
     * @param {Event} event
     */
    function globalCloseHandler(event) {
      if (!(event.target instanceof HTMLElement)) {
        return;
      }

      const dropdownContainsEventTarget = dropdownRef.current?.contains(event.target);
      if (!dropdownContainsEventTarget) {
        /*
          In cases where the module library is being used in shadow DOM,
          the event for this function may be a "composed" event. For our
          purposes this means event.target will be the DOM node that contains
          the shadow root, not the dropdown item so the check above will
          return false.

          For composed events, we can then check the path of the event for
          the element we want.

          https://developer.mozilla.org/en-US/docs/Web/API/Event/composed
          https://javascript.info/shadow-dom-events
        */
        let found = false;
        if (event.composed && dropdownRef.current) {
          // search the path to find if any of the elements
          const path = event.composedPath();
          for (let index = 0; index < path.length; index++) {
            const element = path[index];
            if (element instanceof Node && dropdownRef.current.contains(element)) {
              found = true;
              break;
            }
          }
        }

        if (!found) {
          setIsOpen(false);
        }
      }
    }

    if (isOpen) {
      window.addEventListener('click', globalCloseHandler);
      return () => {
        window.removeEventListener('click', globalCloseHandler);
      };
    }
    return undefined;
  }, [isOpen, onOpen]);

  const handleOpen = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) {
        return;
      }

      onOpen?.(!isOpen);
      setIsOpen(!isOpen);
    },
    [disabled, onOpen, isOpen],
  );

  const handleSelect = (event, val) => {
    event.preventDefault();
    event.stopPropagation();
    if (val.submenu) {
      const newArray = [...submenusOpen];
      if (newArray.includes(val.label)) {
        newArray.splice(newArray.indexOf(val.label), 1);
      } else {
        newArray.push(val.label);
      }
      setSubmenusOpen(newArray);
    } else {
      setValue(val.label);
      setIsOpen(false);

      onChange(val.value);
    }
  };

  const doItemsHaveIcons = menuItems.some((item) => item.icon);
  return (
    <div className={`${className} ${isRebrand ? 'brand--new' : ''}`} ref={dropdownRef}>
      <ClickableElement onClick={handleOpen}>
        {type === 'box' || type === 'underline' ? (
          <div className={type}>
            <span className="text" title={value}>
              {value}
            </span>
            <div className="arrow">
              <ArrowBig title={title} size="3" direction={isOpen ? 'up' : 'down'} />
            </div>
          </div>
        ) : (
          <div className={`dots${type === 'dots-horizontal' ? ' rotated' : ''}`}>
            <Menu title={title} size="8" />
          </div>
        )}
      </ClickableElement>
      {isOpen && (
        <>
          <div className={`list-container align-${align} dir-${direction}`}>
            <div className="list">
              {menuItems.map((option) => {
                const Icon = option.icon;
                const optGroup = option.submenu;
                if (optGroup) {
                  return (
                    <span key={option.label}>
                      <ClickableElement
                        className="list-item submenu"
                        key={option.label}
                        onClick={(event) => handleSelect(event, option)}
                      >
                        {!Icon && doItemsHaveIcons && <div className="icon-placeholder" />}
                        <span>{option.label}</span>
                        <ArrowBig
                          size="3"
                          direction={submenusOpen.includes(option.label) ? 'up' : 'down'}
                        />
                      </ClickableElement>

                      {submenusOpen.includes(option.label) &&
                        optGroup.map((subOption) => (
                          <ClickableElement
                            className="submenu-item"
                            key={subOption.label}
                            onClick={(event) => handleSelect(event, subOption)}
                          >
                            <span>{subOption.label}</span>
                          </ClickableElement>
                        ))}
                    </span>
                  );
                }
                return (
                  <ClickableElement
                    className="list-item"
                    key={option.label}
                    onClick={(event) => handleSelect(event, option)}
                  >
                    {Icon && (
                      <Icon
                        size={`${option.size ?? iconSizes[size]}`}
                        fill={colors.boulder}
                        className={option.iconClassName}
                      />
                    )}
                    {!Icon && doItemsHaveIcons && <div className="icon-placeholder" />}
                    <span>{option.label}</span>
                  </ClickableElement>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

DropDown.propTypes = {
  /** Emotion base style */
  className: PropTypes.string.isRequired,
  /** A list of objects containing the values of the menu items */
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
      iconClassName: PropTypes.string,
      label: PropTypes.string.isRequired,
      size: PropTypes.number,
      value: PropTypes.string.isRequired,
      submenu: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        }),
      ),
    }),
  ).isRequired,
  /** A function to execute on select change */
  onChange: PropTypes.func.isRequired,
  /** Pass menu open events to parents */
  onOpen: PropTypes.func,
  /** Optional default value */
  defaultValue: PropTypes.string,
  /** Style of the drop down */
  type: PropTypes.oneOf(['box', 'underline', 'dots-vertical', 'dots-horizontal']),
  /** Size of the drop down */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** where should the drop down align to */
  align: PropTypes.oneOf(['left', 'right']),
  /** which direction the dropdown should open */
  direction: PropTypes.oneOf(['down', 'up']),
  /** Should the drop down be clickable */
  disabled: PropTypes.bool,
  /** SVG title */
  title: PropTypes.string,
  isRebrand: PropTypes.bool,
};

DropDown.defaultProps = {
  align: 'right',
  defaultValue: 'Select',
  direction: 'down',
  disabled: false,
  onOpen: () => {},
  size: 'medium',
  title: '',
  type: 'box',
  isRebrand: false,
};

/**
 * @typedef {import('./DropDown').DropDownSize} DropDownSize
 */

/** @type {Record<DropDownSize, string>} */
const heights = {
  small: '1.812rem',
  medium: '2rem',
  large: '2.187rem',
};

/** @type {Record<DropDownSize, string>} */
const fontSizes = {
  small: '0.812rem',
  medium: '0.875rem',
  large: '1rem',
};

/** @type {Record<DropDownSize, string>} */
const margins = {
  small: '1.125rem',
  medium: '1.25rem',
  large: '1.4375rem',
};

/** @param {import('.').DropDownProps} props */
const getSizeDependentStyles = (props) => {
  const doItemsHaveIcons = props.menuItems.some((item) => item.icon);
  const styles = {
    small: `
      font-size: 0.812rem;
      height: 1.8rem;
      padding: 0 ${doItemsHaveIcons ? '0.375rem' : '1.1875rem'};
      padding: 0 1.1875rem;
    `,
    medium: `
      font-size: 0.875rem;
      height: 2rem;
      padding: 0 ${doItemsHaveIcons ? '0.5625rem' : '1.375rem'};
      padding: 0 1.375rem;
    `,
    large: `
      font-size: 1rem;
      height: 2.2rem;
      padding: 0 ${doItemsHaveIcons ? '0.75rem' : '1.5625rem'};
      padding: 0 1.5625rem;
    `,
    smallSub: `
      font-size: 0.75rem;
      height: 1.625rem;
      padding: 0 1.625rem 0 ${doItemsHaveIcons ? '3.875rem' : '2.1875rem'};
    `,
    mediumSub: `
      font-size: 0.812rem;
      height: 1.75rem;
      padding: 0 1.75rem 0 ${doItemsHaveIcons ? '4.1875rem' : '2.5rem'};
    `,
    largeSub: `
      font-size: 0.875rem;
      height: 1.875rem;
      padding: 0 1.875rem 0 ${doItemsHaveIcons ? '4.5rem' : '2.8125rem'};
    `,
  };

  return styles;
};

const hasSubMenus = (items) => items.some((menu) => (menu.submenu?.length ?? 0) > 0);
const longestLength = (value, menu) => Math.max(value, menu.label.length);

function longestMenuItem(value, menu) {
  const longest = Math.max(value, menu.label.length);
  if (menu.submenu) {
    return Math.max(longest, menu.submenu.reduce(longestLength, 0));
  }

  return longest;
}

/** @param {import('.').DropDownProps} props */
const dotStyles = (props) => `
width: 3.125rem;
display: inline-block;

.list {
  min-width: 100%;
  ${
    hasSubMenus(props.menuItems)
      ? `width: ${props.menuItems.reduce(longestMenuItem, 0) * 1.5}rem;`
      : ''
  }
}

.dots {
  display: inline-block;

  &.rotated {
    transform: rotate(90deg);
  }
}
`;

/** @param {import('.').DropDownProps} props */
const boxStyles = (props) => `
.list {
  max-width: 14.375rem;
  min-width: 100%;
  ${
    hasSubMenus(props.menuItems)
      ? `width: ${props.menuItems.reduce(longestMenuItem, 0) * 0.625}rem;`
      : ''
  }

  span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.text {
  font-size: ${fontSizes[props.size]};
}

.box {
  align-items: center;
  border: 0.0625rem solid ${colors.silverSand};
  border-radius: 0.125rem;
  display: flex;
  justify-content: space-between;
  height: ${heights[props.size]};

  .text {
    margin-left: ${margins[props.size]};
    margin-right: ${8 / 16}rem; ${'' /* add space between arrow & text */}
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  svg {
    transform: rotate(90deg);
    margin-right: ${margins[props.size]};
  }
}

.underline {
  border-bottom: 0.125rem solid ${colors.silverSandAlt};
  border-radius: 0.125rem;
  display: flex;
  justify-content: space-between;
  height: ${heights[props.size]};

  svg {
    transform: rotate(90deg);
    margin-right: 1.437rem;
    margin-top: 0.375rem;
  }
}
`;

const styledDropDown = styled(DropDown)`
  ${(props) => (props.isRebrand ? mediumInter() : medium())}
  color: ${(props) => (props.isRebrand ? colors.disableGrey : colors.boulder)};

  .outside {
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    position: fixed;
    cursor: default;
  }

  .list-container {
    position: relative;
    z-index: 1;

    .list {
      position: absolute;
      background: ${colors.white};
      box-shadow: 0 0.187rem 0.375rem ${colors.boxShadow};
      padding: 0;
      max-height: 16rem;
      overflow-y: auto;

      .list-item {
        border-width: 0;
        text-align: left;
        display: flex;
        align-items: center;

        svg,
        .icon-placeholder {
          margin-right: ${(props) => iconMargins[props.size]};
          width: ${(props) => iconWidths[props.size]};
          vertical-align: middle;
        }

        &.submenu {
          svg {
            vertical-align: middle;
            width: unset;
            margin-left: 0.5rem;
          }
        }

        &:hover {
          background: ${colors.whiteSmoke};
        }

        ${(props) => getSizeDependentStyles(props)[props.size]}
      }

      .submenu-item {
        text-align: left;
        padding-left: 3.75rem;
        display: flex;
        align-items: center;

        &:hover {
          background: ${colors.whiteSmoke};
        }

        ${(props) => getSizeDependentStyles(props)[`${props.size}Sub`]}
      }
    }

    &.align-right {
      .list {
        right: 0;
      }
    }

    &.dir-up {
      .list {
        ${(props) => `bottom: ${heights[props.size]}`}
      }
    }

    .arrow {
      height: ${8.5 / 16}rem;

      svg {
        height: 100%;
      }
    }
  }

  ${(props) => (props.type.includes('dots') ? dotStyles(props) : boxStyles(props))}
`;

export default styledDropDown;
